import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CropMonitorRecord } from '@/types/api';
import { cropApi } from '@/services/cropApi';
import DashboardHeader from './components/DashboardHeader';
import LatestDataPanel from './components/LatestDataPanel';
import CameraControl from './components/CameraControl';
import DeviceSettings from './components/DeviceSettings';
import ChartPanel from './components/ChartPanel';
import HistoryTable from './components/HistoryTable';
import CaptureModal from '@/pages/devices/components/CaptureModal';
import Toast from '@/pages/devices/components/Toast';
import type { ToastMessage } from '@/pages/devices/components/Toast';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [deviceId, setDeviceId] = useState('');
  const [latest, setLatest] = useState<CropMonitorRecord | null>(null);
  const [selectedData, setSelectedData] = useState<CropMonitorRecord | null>(null);
  const [totalCnt, setTotalCnt] = useState(0);
  const [housingHeightMm, setHousingHeightMm] = useState(2200);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Capture state
  const [isOnline, setIsOnline] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const toastIdRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const addToast = useCallback((text: string, type: ToastMessage['type']) => {
    toastIdRef.current += 1;
    const id = toastIdRef.current;
    setToasts((prev) => [...prev, { id, text, type }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const cleanupCaptureTimers = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const loadLatest = useCallback(async (id: string) => {
    try {
      const res = await cropApi.getLatest(id);
      if (res.result === 'success' && res.data) {
        setLatest(res.data);
        if (res.housing_height_mm) {
          setHousingHeightMm(res.housing_height_mm);
        }
      } else {
        setLatest(null);
      }
    } catch {
      setError('최신 데이터를 불러오지 못했습니다.');
    }
  }, []);

  // Fetch device online status + last capture
  const fetchDeviceStatus = useCallback(async (id: string) => {
    try {
      const data = await cropApi.getSettings(id);
      if (!mountedRef.current) return;
      if (data.result === 'success') {
        setIsOnline(data.is_online === 'Y');
      }
    } catch {
      // silently ignore
    }
  }, []);

  // Initial load
  useEffect(() => {
    const id = localStorage.getItem('device_id');
    if (!id) {
      navigate('/');
      return;
    }
    setDeviceId(id);

    const init = async () => {
      setLoading(true);
      await Promise.all([loadLatest(id), fetchDeviceStatus(id)]);
      setLoading(false);
    };
    init();

    return () => {
      mountedRef.current = false;
    };
  }, [navigate, loadLatest, fetchDeviceStatus]);

  // 30s periodic device status + latest data refresh
  useEffect(() => {
    if (!deviceId) return;
    mountedRef.current = true;

    statusTimerRef.current = setInterval(() => {
      fetchDeviceStatus(deviceId);
      loadLatest(deviceId);
    }, 30000);

    return () => {
      mountedRef.current = false;
      if (statusTimerRef.current) clearInterval(statusTimerRef.current);
      cleanupCaptureTimers();
    };
  }, [deviceId, fetchDeviceStatus, loadLatest, cleanupCaptureTimers]);

  // --- Capture Flow ---

  const startCapturePolling = useCallback(() => {
    let pollCount = 0;
    const maxPoll = 36; // 5s * 36 = 180s = 3min
    const pollInterval = 5000;

    cleanupCaptureTimers();

    pollTimerRef.current = setInterval(async () => {
      pollCount += 1;
      try {
        const data = await cropApi.getCaptureStatus(deviceId);
        if (!mountedRef.current) return;

        if (data.status === 'done') {
          cleanupCaptureTimers();
          setIsCapturing(false);
          const timeStr = data.capture_time || '';

          // Refresh dashboard data (images / measurements / regdate)
          if (deviceId) {
            loadLatest(deviceId);
            fetchDeviceStatus(deviceId);
            setRefreshTrigger((prev) => prev + 1);
          }

          addToast(`촬영 완료 (${timeStr.slice(11, 19)})`, 'success');
        } else if (data.status === 'fail') {
          cleanupCaptureTimers();
          setIsCapturing(false);
          addToast('촬영 실패', 'error');
        }
        // 'capturing' or 'none' → continue polling
      } catch {
        // polling error — continue
      }

      if (pollCount >= maxPoll) {
        cleanupCaptureTimers();
        setIsCapturing(false);
        addToast('촬영 결과를 확인할 수 없습니다. 장비 상태를 확인하세요.', 'warning');
      }
    }, pollInterval);
  }, [deviceId, addToast, cleanupCaptureTimers, loadLatest, fetchDeviceStatus]);

  const handleCaptureConfirm = useCallback(async () => {
    setShowCaptureModal(false);
    if (!deviceId || isCapturing) return;

    setIsCapturing(true);
    addToast('촬영 명령을 전송했습니다. 완료까지 약 1분 소요됩니다.', 'info');

    try {
      const res = await cropApi.sendCapture(deviceId);
      if (!mountedRef.current) return;

      if (res.result === 'success') {
        startCapturePolling();
      } else {
        setIsCapturing(false);
        cleanupCaptureTimers();
        addToast(res.message || '촬영 명령 전송 실패', 'error');
      }
    } catch {
      if (!mountedRef.current) return;
      setIsCapturing(false);
      cleanupCaptureTimers();
      addToast('촬영 명령 전송 중 오류가 발생했습니다.', 'error');
    }
  }, [deviceId, isCapturing, addToast, cleanupCaptureTimers, startCapturePolling]);

  // ---

  const handleSelectRecord = useCallback((record: CropMonitorRecord) => {
    setSelectedData(record);
  }, []);

  const handleBackToLatest = useCallback(() => {
    setSelectedData(null);
  }, []);

  const displayRecord = selectedData ?? latest;
  const isSelected = selectedData !== null;

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0f2d1a 0%, #1B4F72 60%, #145a32 100%)' }}
      >
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line text-white text-5xl animate-spin"></i>
          </div>
          <p className="text-white text-sm font-medium">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f4f8f5' }}>
      <DashboardHeader deviceId={deviceId} />

      <main className="max-w-screen-xl mx-auto px-4 md:px-6 py-5 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2 text-red-600 text-sm">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-error-warning-line"></i>
            </div>
            {error}
          </div>
        )}

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              icon: 'ri-leaf-line',
              label: '최신 NDVI',
              value: latest ? latest.ndvi_avg.toFixed(4) : '-',
              color: '#27ae60',
            },
            {
              icon: 'ri-focus-3-line',
              label: '최신 ToF 거리',
              value: latest ? `${Number(latest.tof_center_mm).toFixed(0)} mm` : '-',
              color: '#2E86C1',
            },
            {
              icon: 'ri-plant-line',
              label: '최신 벼 높이',
              value: latest ? `${Number(latest.rice_height_mm).toFixed(0)} mm` : '-',
              color: '#1B4F72',
            },
            {
              icon: 'ri-time-line',
              label: '최신 수신',
              value: latest ? latest.regdate.substring(0, 10) : '-',
              color: '#e67e22',
            },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-lg"
                  style={{ backgroundColor: stat.color + '18' }}
                >
                  <i className={`${stat.icon} text-sm`} style={{ color: stat.color }}></i>
                </div>
                <span className="text-xs text-gray-500">{stat.label}</span>
              </div>
              <p className="font-bold text-gray-800 text-base">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Capture Card */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div
            className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between"
            style={{ backgroundColor: '#f0f9f4' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-camera-line text-green-600"></i>
              </div>
              <span className="font-bold text-gray-800 text-sm">수동 촬영</span>
            </div>
            <button
              onClick={() => fetchDeviceStatus(deviceId)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-white/80 transition-colors cursor-pointer whitespace-nowrap border border-gray-200"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line"></i>
              </div>
              새로고침
            </button>
          </div>

          <div className="p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Status Info */}
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-3">
                {isOnline ? (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: '#27ae6018', color: '#27ae60' }}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: '#27ae60' }}></span>
                    온라인
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: '#95a5a618', color: '#95a5a6' }}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: '#95a5a6' }}></span>
                    오프라인
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  마지막 촬영: <span className="text-gray-600 font-medium">{latest?.regdate || '-'}</span>
                </span>
              </div>
            </div>

            {/* Capture Button */}
            {isCapturing ? (
              <button
                disabled
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold cursor-not-allowed whitespace-nowrap min-w-[140px] transition-all"
                style={{ backgroundColor: '#27ae6040', color: '#27ae60' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin"></i>
                </div>
                촬영 중...
              </button>
            ) : isOnline ? (
              <button
                onClick={() => setShowCaptureModal(true)}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap min-w-[140px] transition-all hover:brightness-110"
                style={{ backgroundColor: '#27ae60' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-camera-line"></i>
                </div>
                촬영
              </button>
            ) : (
              <div className="relative group">
                <button
                  disabled
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold cursor-not-allowed whitespace-nowrap min-w-[140px]"
                  style={{ backgroundColor: '#d5dbdb', color: '#95a5a6' }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-camera-line"></i>
                  </div>
                  촬영
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  장비가 오프라인입니다
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Latest / Selected Data */}
        {displayRecord ? (
          <LatestDataPanel
            record={displayRecord}
            totalCnt={totalCnt}
            housingHeightMm={housingHeightMm}
            isSelected={isSelected}
            onBackToLatest={handleBackToLatest}
          />
        ) : (
          <div className="bg-white rounded-2xl p-10 text-center text-gray-400 border border-gray-100">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-inbox-line text-5xl"></i>
            </div>
            <p>촬영 데이터가 없습니다.</p>
          </div>
        )}

        {/* Camera Angle Control */}
        <CameraControl deviceId={deviceId} />

        {/* Device Remote Settings */}
        <DeviceSettings deviceId={deviceId} housingHeightMm={housingHeightMm} />

        {/* Charts */}
        <ChartPanel deviceId={deviceId} refreshTrigger={refreshTrigger} />

        {/* History Table */}
        <HistoryTable
          deviceId={deviceId}
          selectedNo={selectedData?.no ?? null}
          onSelectRecord={handleSelectRecord}
          onTotalCntChange={setTotalCnt}
          refreshTrigger={refreshTrigger}
        />
      </main>

      {/* Footer */}
      <footer className="mt-8 py-5 text-center" style={{ backgroundColor: '#1B4F72' }}>
        <p className="text-white/60 text-xs">
          © 2026 Rice Crop Monitoring System — Device:{' '}
          <span className="text-white font-semibold">{deviceId}</span>
        </p>
      </footer>

      {/* Capture Modal */}
      {showCaptureModal && (
        <CaptureModal
          deviceName=""
          deviceId={deviceId}
          onCancel={() => setShowCaptureModal(false)}
          onConfirm={handleCaptureConfirm}
          message="지금 즉시 촬영을 실행합니다. 약 40~60초 소요됩니다."
        />
      )}

      {/* Toast */}
      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
}